import {
  collectMatches,
  formatOdds,
  kickoffTime,
  localizeLeague,
  resolveApiBase,
  SportsApiClient,
  type NormalizedMatch,
  type OddsFormat,
  type WidgetProps,
} from '@sports/core'
import type { ComponentType } from 'preact'
import { useState } from 'preact/hooks'
import { useFeed } from '../../use-feed'
import { useLabels } from '../../use-labels'
import { useTimeZone } from '../../use-time-zone'
import { TeamCrest } from './team-crest'

/**
 * The betbrothers.es match-center for GoalServe sports (basketball, hockey, …).
 * Same look and behaviour as the football one: an all-dates calendar grouped by
 * date (then league), a month date-picker, viewer-local kickoff times, boxed
 * 1/X/2 odds with the favourite in yellow and the bookmaker logo per row. It
 * pulls a week of day-feeds (livescores(sport, 0..7)) and merges them, since the
 * GoalServe feed is per-day. Registered per sport via createGoalserveMatchCenter().
 */
export interface GoalserveMatchCenterConfig {
  sport: string
  oddsCat?: string
  threeWay?: boolean
  defaultHeading: string
  /** logotips sport slug for crests; undefined = no logos (tennis/handball/…). */
  logoSport?: string
  /**
   * Case-insensitive regex (source string) of league/category names to DROP.
   * Tennis uses it to hide doubles and junior qualification draws — the feed
   * lists them alongside the ATP/WTA main singles, but bookmakers don't price
   * them, so they'd fill the list with odds-less rows and bury the real matches.
   */
  excludeLeague?: string
  /**
   * Case-insensitive regex (source string): when set, ONLY league/category names
   * matching it are kept. Hockey uses it to show just the marquee competitions
   * (NHL, KHL, IIHF Worlds, Olympics, Champions Hockey…) and drop the preseason
   * / friendly / minor-league noise GoalServe mixes into the feed.
   */
  includeLeague?: string
}

export const GS_MATCH_CENTER_ATTRIBUTES = [
  'competition',
  'include',
  'lang',
  'limit',
  'heading',
  'refresh',
  'api-base',
  'theme',
] as const

interface Props extends WidgetProps {
  competition?: string
  /**
   * Keep only leagues matching this regex — a per-page override of the widget's
   * own `includeLeague`. The combined tennis page uses `include="atp|wta"` to
   * show the main tours without the ITF / Challenger long tail.
   */
  include?: string
  lang?: string
  limit?: string
  heading?: string
  refresh?: string
  apiBase?: string
}

interface MatchOdds {
  home: number | null
  draw: number | null
  away: number | null
}
interface BookmakerResponse {
  oddsFormat: OddsFormat
  brand: { name?: string; logo?: string | null; link?: string | null } | null
}

const LIVE = /^\d{1,3}'?$|q[1-4]|p[1-3]|set|period|half|\bht\b|\bot\b|\bso\b|live|in progress/i
const FINISHED = /finished|final|\bft\b|after|ended/i
// Upcoming window (default view). GoalServe feeds are per-day, so we pull a
// range and merge. "Resultados" additionally needs the recent PAST days — the
// feed is forward-only otherwise, so finished matches never load and the
// Resultados toggle looked broken (empty of real results).
const DAYS = [0, 1, 2, 3, 4, 5, 6, 7]
const RESULTS_DAYS = [-7, -6, -5, -4, -3, -2, -1]

/**
 * Esports @type → clean display label. GoalServe writes the game as the match's
 * `@type` (e.g. "CS GO", "League Of Legends", "DOTA 2"), surfaced as
 * `m.discipline`. We normalise it to a short label shown on the esports league
 * header alongside the tournament name. Only CS/LoL/Dota carry live data today;
 * the rest are mapped and ready for when GoalServe starts carrying them.
 */
const ESPORTS_GAME_LABELS: { re: RegExp; label: string }[] = [
  { re: /cs\s?go|cs2|counter-?strike/i, label: 'CS2' },
  { re: /valorant/i, label: 'Valorant' },
  { re: /lol|league of legends/i, label: 'LoL' },
  { re: /dota/i, label: 'Dota 2' },
  { re: /fortnite/i, label: 'Fortnite' },
  { re: /rocket ?league/i, label: 'Rocket League' },
  { re: /starcraft|sc2/i, label: 'Starcraft' },
  { re: /overwatch/i, label: 'Overwatch' },
]
function esportsGameLabel(discipline?: string): string {
  const raw = discipline?.trim()
  if (!raw) return ''
  return ESPORTS_GAME_LABELS.find((g) => g.re.test(raw))?.label ?? raw
}

const PICKER_LOCALE: Record<string, string> = {
  en: 'en-GB', es: 'es-ES', de: 'de-DE', fr: 'fr-FR', pt: 'pt-PT', pl: 'pl-PL',
  ru: 'ru-RU', el: 'el-GR', hr: 'hr-HR', sr: 'sr-RS', bs: 'bs-BA', lv: 'lv-LV',
  uk: 'en-GB', ie: 'en-IE', mx: 'es-MX', pe: 'es-PE',
}

function number(value: string | undefined, fallback: number): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}
/** GoalServe "dd.MM.yyyy" → sortable yyyy-mm-dd, or '' when not that shape. */
function dayKey(date?: string): string {
  if (!date || !/^\d{1,2}\.\d{1,2}\.\d{4}$/.test(date)) return ''
  const [d, m, y] = date.split('.')
  return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`
}
function ddmmyyyy(key: string): string {
  const [y, m, d] = key.split('-')
  return y && m && d ? `${d}/${m}/${y}` : key
}
/** Feed kickoff (UTC) → wall-clock time in the site's zone (see siteTimeZone). */
function localTime(date: string | undefined, time: string | undefined, lang: string, tz?: string): string {
  const k = dayKey(date)
  if (!k) return time ?? ''
  const [hh = '00', mm = '00'] = (time ?? '').split(':')
  const dt = new Date(`${k}T${hh.padStart(2, '0')}:${mm.padStart(2, '0')}:00Z`)
  return kickoffTime(dt, lang, tz) || (time ?? '')
}
function bestOf(values: (number | null | undefined)[]): number | null {
  const present = values.filter((v): v is number => v != null && Number.isFinite(v))
  return present.length ? Math.min(...present) : null
}
/** 42-cell month grid (6 weeks, Sunday-first). */
function monthGrid(y: number, m: number): { key: string; day: number; inMonth: boolean }[] {
  const startDow = new Date(Date.UTC(y, m, 1)).getUTCDay()
  const start = Date.UTC(y, m, 1 - startDow)
  const cells = []
  for (let i = 0; i < 42; i++) {
    const d = new Date(start + i * 86_400_000)
    const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`
    cells.push({ key, day: d.getUTCDate(), inMonth: d.getUTCMonth() === m })
  }
  return cells
}
function monthLabel(y: number, m: number, lang: string): string {
  const s = new Date(Date.UTC(y, m, 1)).toLocaleDateString(PICKER_LOCALE[lang] ?? 'en-GB', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  })
  return s.charAt(0).toUpperCase() + s.slice(1)
}

interface League {
  name: string
  id?: string
  matches: NormalizedMatch[]
}
interface DateGroup {
  day: string
  count: number
  leagues: League[]
}

export function createGoalserveMatchCenter(config: GoalserveMatchCenterConfig): ComponentType<Props> {
  return function GoalserveMatchCenter({
    competition,
    include,
    lang = 'es',
    limit,
    heading,
    refresh,
    apiBase,
  }: Props) {
    const base = resolveApiBase(apiBase)
    const refreshSeconds = number(refresh, 60)
    const max = number(limit, 20)
    const client = new SportsApiClient(base)
    const t = useLabels(base, lang)
    const tz = useTimeZone()
    const [showFinished, setShowFinished] = useState(false)
    const [shownLimit, setShownLimit] = useState(max)
    const [leagueFilter, setLeagueFilter] = useState<string | null>(null)
    const [gameFilter, setGameFilter] = useState<string | null>(null)
    const [fromKey, setFromKey] = useState<string | null>(null)
    const [pickerOpen, setPickerOpen] = useState(false)
    const now0 = new Date()
    const [viewMonth, setViewMonth] = useState({ y: now0.getFullYear(), m: now0.getMonth() })

    // A window of per-day feeds, merged. Default = upcoming (0..+7); with
    // "Resultados" on we also pull the last week so finished matches appear.
    const days = showFinished ? [...RESULTS_DAYS, ...DAYS] : DAYS
    const { data, error, loading } = useFeed(
      (signal) =>
        Promise.all(
          days.map((d) => client.livescores<unknown>(config.sport, d, signal).catch(() => null)),
        ),
      [base, config.sport, showFinished],
      refreshSeconds,
    )
    const { data: display } = useFeed(
      (signal) => client.bookmaker<BookmakerResponse>(lang, 'match-center', signal),
      [base, lang],
      0,
    )

    const format: OddsFormat = display?.oddsFormat ?? 'decimal'
    const brand = display?.brand ?? null
    const link = brand?.link ?? undefined

    const needle = competition?.trim().toLowerCase()
    // Esports splits by GAME (@type → m.discipline), not league name — a CS page
    // must not match on the tournament name. Map the page's competition value to
    // the discipline it means; falls back to a plain league-name match otherwise.
    const ESPORTS_GAME: Record<string, RegExp> = {
      cs: /cs\s?go|cs2|counter-?strike/i,
      csgo: /cs\s?go|cs2|counter-?strike/i,
      cs2: /cs\s?go|cs2|counter-?strike/i,
      valorant: /valorant/i,
      lol: /lol|league of legends/i,
      dota: /dota/i,
      fortnite: /fortnite/i,
      rocketleague: /rocket ?league/i,
      starcraft: /starcraft|sc2/i,
      overwatch: /overwatch/i,
    }
    const gameRe = config.sport === 'esports' && needle ? ESPORTS_GAME[needle] : null
    const excludeRe = config.excludeLeague ? new RegExp(config.excludeLeague, 'i') : null
    const includePattern = include?.trim() || config.includeLeague
    const includeRe = includePattern ? new RegExp(includePattern, 'i') : null
    const merged = Array.isArray(data) ? data.flatMap((d) => (d ? collectMatches(d, 300) : [])) : []
    // Dedupe by id (a match can appear in adjacent day-feeds).
    const seen = new Set<string>()
    const dedup: NormalizedMatch[] = []
    for (const m of merged) {
      if (m.id && !seen.has(m.id)) {
        seen.add(m.id)
        dedup.push(m)
      }
    }
    const matchDays = new Set(dedup.map((m) => dayKey(m.date)).filter(Boolean))
    const dateFiltered = dedup
      .filter((m) =>
        !needle
          ? true
          : gameRe
            ? gameRe.test(m.discipline ?? '') || gameRe.test(m.league ?? '')
            : (m.league ?? '').toLowerCase().includes(needle),
      )
      .filter((m) => !includeRe || includeRe.test(m.league ?? ''))
      .filter((m) => !excludeRe || !excludeRe.test(m.league ?? ''))
      .filter((m) => showFinished || !FINISHED.test(m.status))
      .filter((m) => !fromKey || dayKey(m.date) === fromKey)
    // Esports can be filtered by GAME (discipline) in addition to league — the
    // general esports page groups many games, so a game dropdown is the primary
    // filter there. Games list is from the full set; selecting one narrows the
    // league dropdown + the shown matches.
    const isEsports = config.sport === 'esports'
    const games = isEsports
      ? ([...new Set(dateFiltered.map((m) => esportsGameLabel(m.discipline)).filter(Boolean))].sort() as string[])
      : []
    const gameFiltered = gameFilter
      ? dateFiltered.filter((m) => esportsGameLabel(m.discipline) === gameFilter)
      : dateFiltered
    // Leagues present (for the "Todas las ligas" dropdown) + active filter.
    const leagues = [...new Set(gameFiltered.map((m) => m.league).filter(Boolean))].sort() as string[]
    const all = gameFiltered
      .filter((m) => !leagueFilter || m.league === leagueFilter)
      .sort((a, b) =>
        `${dayKey(a.date)} ${a.time ?? ''}`.localeCompare(`${dayKey(b.date)} ${b.time ?? ''}`),
      )
    const shown = all.slice(0, shownLimit)
    const sortedDays = [...matchDays].sort()
    const stepDay = (dir: 1 | -1) => {
      if (!fromKey) {
        setFromKey(dir === 1 ? sortedDays[0] : sortedDays[sortedDays.length - 1])
      } else {
        const i = sortedDays.indexOf(fromKey)
        setFromKey(sortedDays[i + dir] ?? (dir === -1 ? null : fromKey))
      }
      setShownLimit(max)
    }

    // Group shown matches by date, then by league within each date.
    const dateGroups: DateGroup[] = []
    for (const m of shown) {
      const dk = dayKey(m.date)
      let dg = dateGroups[dateGroups.length - 1]
      if (!dg || dg.day !== dk) {
        dg = { day: dk, count: 0, leagues: [] }
        dateGroups.push(dg)
      }
      dg.count += 1
      const lname = m.league ?? '—'
      let lg = dg.leagues.find((x) => x.name === lname)
      if (!lg) {
        lg = { name: lname, id: m.leagueId, matches: [] }
        dg.leagues.push(lg)
      }
      lg.matches.push(m)
    }

    // Resolve odds for the matches on screen — GoalServe by id, then name-pool.
    const refs = shown.map((m) => ({ id: m.id, home: m.home, away: m.away, dateUtc: m.date }))
    const refsKey = refs.map((r) => r.id).join(',')
    const { data: oddsMap } = useFeed(
      (signal) =>
        config.oddsCat && refs.length > 0
          ? client.resolveOdds<Record<string, MatchOdds>>(config.oddsCat, refs, signal)
          : Promise.resolve({} as Record<string, MatchOdds>),
      [base, config.oddsCat, refsKey],
      refreshSeconds,
    )

    // Pass the team name so the API can fall back to a name-based crest source
    // (Flashscore) for teams GoalServe's logotips is missing — notably ~half the
    // NFL teams (e.g. Detroit Lions). Harmless where GoalServe already has the id.
    const teamLogo = (id?: string, name?: string): string | undefined =>
      config.logoSport && id
        ? `${base}/v1/logos/${config.logoSport}/teams/${id}` +
          (name ? `?name=${encodeURIComponent(name)}` : '')
        : undefined
    const leagueLogo = (id?: string): string | undefined =>
      config.logoSport && id ? `${base}/v1/logos/${config.logoSport}/leagues/${id}` : undefined

    const cell = (label: string, price: number | null | undefined, best: boolean) => {
      const text = price != null ? formatOdds(price, format) : ''
      const cls = `cell${best && text ? ' best' : ''}${text ? '' : ' empty'}`
      const inner = (
        <>
          <span class="k">{label}</span>
          {text || '—'}
        </>
      )
      return link && text ? (
        <a class={cls} href={link} target="_blank" rel="sponsored noopener">
          {inner}
        </a>
      ) : (
        <span class={cls}>{inner}</span>
      )
    }
    const bookmaker = () => {
      const inner = brand?.logo ? <img src={brand.logo} alt={brand.name ?? ''} /> : (brand?.name ?? '')
      if (!brand?.logo && !brand?.name) return <span class="bk" />
      return link ? (
        <a class="bk" href={link} target="_blank" rel="sponsored noopener">
          {inner}
        </a>
      ) : (
        <span class="bk">{inner}</span>
      )
    }
    const row = (m: NormalizedMatch) => {
      // A match only counts as live/played when it actually carries a score —
      // GoalServe sometimes tags a scheduled (often future-dated) match with a
      // bare code like status "1", which the LIVE minute-regex would otherwise
      // mistake for an in-play minute and render as "0:0".
      const hasSets = !!(m.homeSets && m.homeSets.length)
      const hasScore =
        hasSets ||
        (m.homeScore !== '' && m.homeScore != null) ||
        (m.awayScore !== '' && m.awayScore != null)
      const live = LIVE.test(m.status) && hasScore
      const played = hasSets || live || FINISHED.test(m.status)
      const odds = oddsMap?.[m.id]
      const hl = teamLogo(m.homeId, m.home)
      const al = teamLogo(m.awayId, m.away)
      const best = bestOf(config.threeWay ? [odds?.home, odds?.draw, odds?.away] : [odds?.home, odds?.away])
      // Per-set sports → Flashscore layout: two stacked rows, each with its per-
      // set scores, the winner in bold. Tennis shows only the per-set games;
      // volleyball (SofaScore style) also shows the bold sets-won total per side.
      // (Upcoming matches have no sets yet and fall through to the fixture +
      // odds row below.)
      if (m.homeSets && m.homeSets.length) {
        const withTotal = config.sport === 'volleyball'
        const prow = (name: string, total: string, sets: string[], win?: boolean) => (
          <div class={win ? 'tp win' : 'tp'}>
            <span class="nm">{name}</span>
            {withTotal && <span class="tt">{total || 0}</span>}
            <span class="tsets">
              {sets.map((g, i) => (
                <span class="tg" key={i}>
                  {g}
                </span>
              ))}
            </span>
          </div>
        )
        return (
          <div class={live ? 'row live' : 'row'} key={m.id}>
            <span class="time">
              {live ? <span class="st">{m.status}</span> : m.status}
            </span>
            <div class={withTotal ? 'fx tennis-fx vb-fx' : 'fx tennis-fx'}>
              {prow(m.home, m.homeScore, m.homeSets, m.homeWinner)}
              {prow(m.away, m.awayScore, m.awaySets ?? [], m.awayWinner)}
            </div>
            <span class="odds" />
            {config.oddsCat && bookmaker()}
          </div>
        )
      }
      return (
        <div class={live ? 'row live' : 'row'} key={m.id}>
          <span class="time">
            {live ? <span class="st">{m.status}</span> : played ? m.status : localTime(m.date, m.time, lang, tz)}
          </span>
          <span class="fx">
            <span class="side">
              {config.logoSport && <TeamCrest src={hl} name={m.home} />}
              <span class="nm">{m.home}</span>
            </span>
            <span class={played ? 'sc' : 'sc pending'}>
              {played ? `${m.homeScore || 0} : ${m.awayScore || 0}` : t('vs', 'vs')}
            </span>
            <span class="side away">
              {config.logoSport && <TeamCrest src={al} name={m.away} />}
              <span class="nm">{m.away}</span>
            </span>
          </span>
          {played || !config.oddsCat ? (
            <span class="odds" />
          ) : (
            <span class="odds">
              {cell('1', odds?.home, odds?.home != null && odds?.home === best)}
              {config.threeWay && cell('X', odds?.draw, odds?.draw != null && odds?.draw === best)}
              {cell('2', odds?.away, odds?.away != null && odds?.away === best)}
            </span>
          )}
          {config.oddsCat && bookmaker()}
        </div>
      )
    }

    const liveCount = all.filter((m) => LIVE.test(m.status)).length

    return (
      <div class="sports-card">
        <div class="sports-head">
          <span>{heading ?? config.defaultHeading}</span>
          {liveCount > 0 && <span class="live-badge">● {liveCount}</span>}
        </div>

        <div class="bar">
          {isEsports && games.length > 1 && (
            <select
              class="lg-select"
              value={gameFilter ?? ''}
              onChange={(e) => {
                setGameFilter((e.target as HTMLSelectElement).value || null)
                setLeagueFilter(null) // leagues depend on the chosen game
              }}
            >
              <option value="">{t('allGames', 'Todos los juegos')}</option>
              {games.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
            </select>
          )}
          {leagues.length > 1 && (
            <select
              class="lg-select"
              value={leagueFilter ?? ''}
              onChange={(e) => setLeagueFilter((e.target as HTMLSelectElement).value || null)}
            >
              <option value="">{t('allLeagues', 'Todas las ligas')}</option>
              {leagues.map((l) => (
                <option key={l} value={l}>
                  {localizeLeague(l, lang)}
                </option>
              ))}
            </select>
          )}
          <div class="datenav">
            <button
              class="nav"
              aria-label="prev"
              disabled={sortedDays.length === 0 || fromKey === sortedDays[0]}
              onClick={() => stepDay(-1)}
            >
              ‹
            </button>
            <button class="datebtn" onClick={() => setPickerOpen((o) => !o)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <path d="M16 2v4M8 2v4M3 10h18" />
              </svg>
              {fromKey ? ddmmyyyy(fromKey) : t('allDates', 'Todas las fechas')}
            </button>
            <button
              class="nav"
              aria-label="next"
              disabled={sortedDays.length === 0 || fromKey === sortedDays[sortedDays.length - 1]}
              onClick={() => stepDay(1)}
            >
              ›
            </button>
          </div>
          <label class="toggle">
            <input
              type="checkbox"
              checked={showFinished}
              onChange={(e) => setShowFinished((e.target as HTMLInputElement).checked)}
            />
            {t('results', 'Results')}
          </label>
          {pickerOpen && (
            <div class="picker">
              <div class="pk-head">
                <button onClick={() => setViewMonth((v) => (v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 }))}>‹</button>
                <span>{monthLabel(viewMonth.y, viewMonth.m, lang)}</span>
                <button onClick={() => setViewMonth((v) => (v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 }))}>›</button>
              </div>
              <div class="pk-grid pk-dow">
                {Array.from({ length: 7 }, (_, i) =>
                  new Date(Date.UTC(2023, 0, 1 + i)).toLocaleDateString(PICKER_LOCALE[lang] ?? 'en-GB', { weekday: 'short', timeZone: 'UTC' }),
                ).map((w) => (
                  <span key={w}>{w}</span>
                ))}
              </div>
              <div class="pk-grid">
                {monthGrid(viewMonth.y, viewMonth.m).map((c) => (
                  <button
                    key={c.key}
                    class={`pk-day${c.inMonth ? '' : ' out'}${matchDays.has(c.key) ? ' has' : ''}${fromKey === c.key ? ' sel' : ''}`}
                    disabled={!matchDays.has(c.key)}
                    onClick={() => {
                      setFromKey(c.key)
                      setPickerOpen(false)
                      setShownLimit(max)
                    }}
                  >
                    {c.day}
                  </button>
                ))}
              </div>
              <button
                class="pk-all"
                onClick={() => {
                  setFromKey(null)
                  setPickerOpen(false)
                }}
              >
                {t('allDates', 'Todas las fechas')}
              </button>
            </div>
          )}
        </div>

        {loading && <div class="sports-note">{t('loading', 'Loading…')}</div>}
        {!loading && error && (
          <div class="sports-note error">{t('matchesUnavailable', 'Matches are unavailable right now.')}</div>
        )}
        {!loading && !error && shown.length === 0 && (
          <div class="sports-note">{t('noMatches', 'No matches this day.')}</div>
        )}

        {dateGroups.map((dg) => (
          <div key={dg.day}>
            <div class="date-head">
              {ddmmyyyy(dg.day)}
              <span class="count">
                {dg.count === 1 ? t('oneMatch', '1 partido') : `${dg.count} ${t('nMatches', 'partidos')}`}
              </span>
            </div>
            {dg.leagues.map((lg) => {
              const logo = leagueLogo(lg.id)
              // Esports: label the tournament with its game (@type → discipline).
              // A league here is one tournament, so every match shares a game —
              // read it off the first. The client wanted the game name shown "in
              // addition to the competition", e.g. "CS2 · CCT Europa Group Stage".
              const game =
                config.sport === 'esports' ? esportsGameLabel(lg.matches[0]?.discipline) : ''
              return (
                <div key={lg.name}>
                  {/* Only label the league when several are on screen; a single-
                      league page groups by day alone (the widget title already
                      names the league). With many, show its crest if we have one. */}
                  {leagues.length > 1 && (
                    <div class="league">
                      <span class="lg">
                        {logo && <img src={logo} alt="" onError={(e) => ((e.target as HTMLImageElement).style.display = 'none')} />}
                        {game && (
                          <span
                            style="font-size:0.68rem;font-weight:600;color:var(--sports-muted);border:1px solid var(--sports-border);border-radius:4px;padding:0.03rem 0.32rem;white-space:nowrap;"
                          >
                            {game}
                          </span>
                        )}
                        {localizeLeague(lg.name, lang)}
                      </span>
                    </div>
                  )}
                  {lg.matches.map((m) => row(m))}
                </div>
              )
            })}
          </div>
        ))}

        {all.length > shown.length && (
          <button class="more" onClick={() => setShownLimit((l) => l + max)}>
            {t('loadMore', 'Ver más')}
          </button>
        )}
      </div>
    )
  }
}
